import {
  Button,
  Callout,
  Disclaimer,
  Div,
  EditPenIconRed,
  Icon,
  Modal,
  P,
  PropsModal,
  SuccessToast,
  TaxRateFormModal,
  TaxRateRow,
  TrashIconRed,
  canEditTaxRate,
  deleteLocationTaxRate,
  lightTheme,
  reportError,
  useLocationTaxRates,
} from '@vgrubs/components';
import { LocationMode } from 'components/src/types/location.types';
import { LocationTaxRateEntity } from 'components/src/types/taxRate.types';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { DeleteTaxRateModal } from './DeleteTaxRateModal';
import { TaxRateAssignments } from './TaxRateAssignments';

type Props = PropsModal & {
  taxRatesModal: boolean;
  locationId: string | number;
  /** Everything is read-only on a `pos` location. */
  mode: LocationMode | undefined;
  setLoading: Dispatch<SetStateAction<boolean>>;
};

/**
 * The taxes of one location: the list a menu picks from.
 *
 * A `pos` location is read-only end to end - its rates are synced from the
 * register and every write answers 400, so nothing here offers a way in.
 */
export const LocationTaxRatesModal = ({
  taxRatesModal,
  closeModal,
  locationId,
  mode,
  setLoading,
}: Props) => {
  const isPos = mode === LocationMode.pos;

  const { taxRates, isLoading, invalidate } = useLocationTaxRates(locationId, {
    enabled: taxRatesModal,
  });

  const [formModal, setFormModal] = useState<
    LocationTaxRateEntity | boolean | null
  >(null);
  const [deleteModal, setDeleteModal] = useState<LocationTaxRateEntity | null>(
    null
  );
  /** Which rates have their assignments open. */
  const [expandedIds, setExpandedIds] = useState<number[]>([]);

  /**
   * The first load runs behind the page's own spinner and the modal appears
   * with the list already in it - opening onto an empty box that fills itself
   * in a moment later reads as a glitch. A second open is instant: the list is
   * still cached, so `isLoading` never goes true again.
   */
  useEffect(() => {
    if (!taxRatesModal) return;
    setLoading(isLoading);
  }, [taxRatesModal, isLoading, setLoading]);

  const toggleExpanded = (id: number) =>
    setExpandedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );

  async function handleDelete() {
    if (!deleteModal) return;

    try {
      setLoading(true);
      const { data } = await deleteLocationTaxRate(locationId, deleteModal.id);

      SuccessToast(data.message);
      setDeleteModal(null);
      invalidate();
    } catch (error) {
      reportError(error, { context: 'deleteLocationTaxRate' });
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      <Modal
        isOpen={taxRatesModal && !isLoading}
        closeModal={closeModal}
        header="Tax Rates"
        logo={false}
        footer={
          isPos ? undefined : (
            <Button $selected $w="100%" onClick={() => setFormModal(true)}>
              Add Tax
            </Button>
          )
        }
        $numOfFloatingButtons={isPos ? undefined : 1}
      >
        <Div $flex $column $gap="16px">
          {isPos && (
            <Callout tone="info">
              This location runs on a POS, so its taxes are synced from the
              register and cannot be changed here.
            </Callout>
          )}

          {taxRates.length === 0 ? (
            <P $m="24px 0" $center>
              No taxes yet.
            </P>
          ) : (
            <div>
              {taxRates.map((taxRate) => {
                const editable = canEditTaxRate(taxRate, isPos);
                // A rate that applies to everything has no assignments to
                // show, so that one stays a plain row.
                const expandable = !taxRate.applyToAllItems;

                return (
                  <TaxRateRow
                    key={taxRate.id}
                    taxRate={taxRate}
                    showAssignmentCount
                    expanded={expandedIds.includes(taxRate.id)}
                    onToggleExpanded={
                      expandable ? () => toggleExpanded(taxRate.id) : undefined
                    }
                    actions={
                      <>
                        {editable && (
                          <Icon
                            $shadow
                            $pointer
                            $size="32"
                            onClick={() => setFormModal(taxRate)}
                          >
                            <EditPenIconRed />
                          </Icon>
                        )}
                        {editable && (
                          <Icon
                            $pointer
                            onClick={() => setDeleteModal(taxRate)}
                          >
                            <TrashIconRed />
                          </Icon>
                        )}
                      </>
                    }
                  >
                    <TaxRateAssignments assignments={taxRate.assignments} />
                  </TaxRateRow>
                );
              })}
            </div>
          )}

          {!isPos && taxRates.length > 0 && (
            <Disclaimer $m="0" $left $color={lightTheme.darkGray}>
              A tax that applies to all items is charged wherever an item has no
              taxes of its own. Items with their own selection ignore it - set
              those on the menu.
            </Disclaimer>
          )}
        </Div>
      </Modal>

      <TaxRateFormModal
        formModal={formModal}
        closeModal={() => setFormModal(null)}
        locationId={locationId}
        setLoading={setLoading}
        onSaved={() => invalidate()}
      />

      <DeleteTaxRateModal
        deleteModal={deleteModal}
        closeModal={() => setDeleteModal(null)}
        handleDelete={handleDelete}
      />
    </>
  );
};
